import router from './router'
import store from './store'
import { readCredential, readLanguage, readSessionId } from './preprocess'
import { Message } from '@/utils/Message'
import NProgress from 'nprogress' // progress bar
import 'nprogress/nprogress.css' // progress bar style
import { getToken, setToken, getCookiesToken, removeCookiesToken, getUserInfo, setUserInfo } from '@/utils/auth' // get token from cookie
import { syncInitCache, syncAllBasicData, hasCache as noNeedRegainBasicData } from '@/utils/basicDataCache'
import { getRateCacheRange } from '@/api/rate'

// import i18n from './lang' // Internationalization
import { Base64 } from 'js-base64'
import Cookie from 'js-cookie'

import { getAuthUserInfo } from '@/api/auth'
import LocalStorageUtils from '@/utils/localStorageUtils'
import LocalStorageUtilsNew from '@/utils/localStorageUtilsNew'
import DateUtils from '@/utils/dateUtils'
import { getPersonQueryConfig, validateNeedUpdatedGridStatus } from '@/api/commonComponent'
import { getLangMetaTitle } from '@/utils/langUtil'
import { cacheName, cache } from '@/utils/cacheManager'
import lodash from 'lodash'
import { refreshCustomField } from '@/utils/customFields'
import { getBsLimitInfo } from '@/api/tenant'
import { UICustomUtil } from '@/class/cargo/model/utils/UICustomUtil'
import { initPrintTaskMode } from '@/utils/printTaskMode'

NProgress.configure({ showSpinner: false }) // NProgress Configuration

const whiteList = ['/login', '/auth-redirect', '/401'] // no redirect whitelist

function checkIfToRouterNameExists(to, layer) {
  let res = 0
  layer.forEach(l => {
    res += l.children ? checkIfToRouterNameExists(to, l.children) : (l.name === to.name ? 1 : 0)
  })
  return res
}
// 清理本地存储
async function cleanLocalStorage(tenantId, userId) {
  const configModelSet = new Set()
  const configNames = []
  const updateTimes = []
  for (let i = 0; i < localStorage.length; ++i) {
    const key = localStorage.key(i)
    if (key.startsWith(`local_uiDoc_init_gridStatus_${tenantId}_${userId}`) || key.startsWith(`bpmEnabled_${tenantId}`)) {
      localStorage.removeItem(key)
      continue
    }
    if (key.startsWith(`local_uiDoc_person_gridStatus_update_time_${tenantId}_${userId}`)) {
      const configModel = Number(key.replace(`local_uiDoc_person_gridStatus_update_time_${tenantId}_${userId}_`, '').split('_')[0])
      const updateTime = DateUtils.formatStandardDateTime(Number(localStorage.getItem(key)))
      configModelSet.add(configModel)
      configNames.push(key.replace('_update_time', ''))
      updateTimes.push(updateTime)
      continue
    }
  }
  if (configModelSet.size === 0) {
    return
  }
  await validateNeedUpdatedGridStatus({ configModels: [...configModelSet], configNames, updateTimes }).then(res => {
    if (!res.data || res.data.length === 0) {
      return
    }
    const needUpdatedGridKeySet = new Set(res.data)
    for (const item of res.data) {
      needUpdatedGridKeySet.add(item)
      needUpdatedGridKeySet.add(item.replace('local_uiDoc_person_gridStatus', 'local_uiDoc_person_gridStatus_update_time'))
    }
    for (let i = 0; i < localStorage.length; ++i) {
      const key = localStorage.key(i)
      // 只针对当前用户的显示列配置进行清理
      if (key.startsWith(`local_uiDoc_person_gridStatus_${tenantId}_${userId}`) || key.startsWith(`local_uiDoc_person_gridStatus_update_time_${tenantId}_${userId}`)) {
        if (!needUpdatedGridKeySet.has(key)) {
          localStorage.removeItem(key)
        }
      }
    }
  }).catch(() => {})
}
// 重置主题样式
async function resetThemeClass() {
  for (const item of ['default-theme', 'deep-blue-theme']) {
    if (document.body.classList.contains(item)) {
      document.body.classList.remove(item)
    }
  }
  if (Number(cache.hqConfig.get('IsUIPersonalized')) === 1) {
    document.body.classList.add('deep-blue-theme')
    await store.dispatch('settings/changeSetting', { key: 'isUIPersonalized', value: true })
  } else {
    document.body.classList.add('default-theme')
    await store.dispatch('settings/changeSetting', { key: 'isUIPersonalized', value: false })
  }
}
async function rateCacheRange() {
  let monthRange = 18
  await getRateCacheRange().then(res => {
    if (res.ok) {
      const data = res.data
      if (data && data.uuid) {
        monthRange = data.monthRange
      }
    }
  })
  await store.dispatch('settings/changeSetting', { key: 'rateCacheRange', value: monthRange })
}

// 记录多票打开的params
function recordMultipleOpenParams(route) {
  try {
    if (!(route.query.openNew && route.query.key) || !route.params) { return }
    console.log('recordMultipleOpenParams', route)
    const params = route.params
    if (params && Object.keys(params).length > 0) {
      sessionStorage.setItem(route.path + route.query.key + '_params', JSON.stringify(params))
    }
  } catch (err) {
    console.log('记录多票打开的params-失败', err)
  }
}

// 还原多票打开的params
function revertMultipleOpenParams(route) {
  try {
    if (!(route.query.openNew && route.query.key) || !route.params) {
      return
    }
    console.log('revertMultipleOpenParams', route)
    const recordStr = sessionStorage.getItem(route.path + route.query.key + '_params')
    if (!recordStr) {
      return
    }
    const curParams = route.params
    const recordParams = JSON.parse(recordStr)
    if (recordParams && Object.keys(recordParams).length > 0) {
      for (const recordParamsKey in recordParams) {
        if (!curParams[recordParamsKey] && recordParams[recordParamsKey]) {
          curParams[recordParamsKey] = recordParams[recordParamsKey]
        }
      }
    }
  } catch (err) {
    console.log('还原多票打开的params-失败', err)
  } finally {
    // 5s后自动清除key
    setTimeout(() => {
      sessionStorage.removeItem(route.path + route.query.key + '_params')
    }, 5000)
  }
}

router.beforeEach(async(to, from, next) => {
  // start progress bar
  NProgress.start()
  // determine whether the user has logged in
  let hasToken = getToken()
  if (!hasToken) {
    if (getCookiesToken()) {
      hasToken = getCookiesToken()
      setToken(hasToken)
      removeCookiesToken()
    }
  }

  let sessionId = readSessionId()
  // 解析auth认证后回调的凭证
  const credential = readCredential()
  if (credential) {
    const credentialForJson = JSON.parse(Base64.decode(credential))
    const delegationForJson = credentialForJson.delegation
    if (delegationForJson) { // delegate 授权true或者退回授权false
      // 授权或者退出授权时处理
      if (delegationForJson.delegate) {
        sessionId = delegationForJson.sessionId
        LocalStorageUtils.setDelegationInfo(delegationForJson)
      } else {
        sessionId = delegationForJson.sessionId
        LocalStorageUtils.clearDelegationInfo()
      }
    } else {
      sessionId = credentialForJson.sessionId
      LocalStorageUtils.clearDelegationInfo()
    }
    if (credentialForJson.groupKey && credentialForJson.userId) {
      LocalStorageUtils.setConfiguration('sessionGroupKey:' + credentialForJson.userId, credentialForJson.groupKey)
    }
    console.log('delegationForJson sessionId', sessionId, delegationForJson)
  } else {
    const localDelegationInfo = LocalStorageUtils.getDelegationInfo()
    if (sessionId && sessionId !== (localDelegationInfo && localDelegationInfo.sessionId)) { // 如果是通过传参session登录，则进行比较授权的session
      LocalStorageUtils.clearDelegationInfo()
    }
  }

  if (sessionId && sessionId !== hasToken) {
    try {
      await store.dispatch('user/login', sessionId)
      const authUserInfo = await getAuthUserInfo()
      await syncInitCache()
      await rateCacheRange()
      await initPrintTaskMode()
      syncAllBasicData()
      // console.log('获取的auth 用户信息为 ', res)
      // console.log('校验sessionId')
      setUserInfo(authUserInfo.data)
      // console.log('登录成功')
      next({ ...to, replace: true })
    } catch (err) {
      console.log('登录失败', err)
      store.dispatch('user/logout').then(() => {
        next('/login')
        NProgress.done()
      })
    }
  } else if (hasToken) {
    if (to.path === '/login') {
      // if is logged in, redirect to the home page
      next({ path: '/' })
      NProgress.done()
    } else {
      // determine whether the user has obtained his permission roles through getInfo
      const hasRoles = store.getters.roles && store.getters.roles.length > 0
      // console.log('hasRoles====', hasRoles)
      if (hasRoles) {
        // to.query = ''
        // console.log('===hasRoles to', to)
        // console.log('存在hasRoles,校验路由Url')
        if (checkIfToRouterNameExists(to, store.getters.permission_routes)) {
          try {
            const uiCustomization = cache.hqConfig && Number(cache.hqConfig.get('UICustomization')) === 1
            if (uiCustomization) {
              recordMultipleOpenParams(to)
              await UICustomUtil.loadUICustomPageInfo(to.path, store.getters.currentOfficeId)
              revertMultipleOpenParams(to)
            }
          } catch (error) {
            console.log('加载UI自定义页面信息 error', error)
          }
          next()
          NProgress.done()
          return
        } else {
          next('/401')
          NProgress.done()
        }
        // return checkIfToRouterNameExists(to, store.getters.permission_routes) ? next({ ...to, replace: true }) : next('/401')
      } else {
        try {
          console.log('needRegainBasicData()', noNeedRegainBasicData())
          if (!noNeedRegainBasicData()) {
            await syncInitCache()
            await rateCacheRange()
            syncAllBasicData()
          }
          // console.log('获取用户信息')
          // get user info
          // note: roles must be a object array! such as: ['admin'] or ,['developer','editor']
          const { roles } = await store.dispatch('user/getInfo', to.query && to.query.branchId)
          // console.log('设置角色')
          // console.log('roles', roles)
          // generate accessible routes map based on roles
          const accessRoutes = await store.dispatch('permission/generateRoutes', roles)
          // console.log('动态设置路由')
          // dynamically add accessible routes
          router.addRoutes(accessRoutes)
          const currentUserInfo = getUserInfo()
          // 加载参数配置
          const cacheResult = cache.load(cacheName.CONFIG)
          const printModeResult = initPrintTaskMode()
          // 获取计费限制信息
          const bsLimitResult = getBsLimitInfo().then(res => {
            if (res.data) {
              store.dispatch('settings/changeSetting', { key: 'bsLimitInfo', value: res.data })
            }
          })
          // 表格颜色类型
          const colorTypeData = {
            bDefault: true,
            configModule: '10089',
            configName: `local_uiDoc_person_colorModule_${currentUserInfo.tenantId}_${currentUserInfo.userId}_10089`,
            configType: 3, // 固定值，表格配置
            configBy: currentUserInfo.userId,
            tenantId: currentUserInfo.tenantId
          }
          const colorTypeRes = await getPersonQueryConfig(colorTypeData)
          const colorTypeSettings = colorTypeRes.data.find(item => item.bDefault)
          if (colorTypeSettings && colorTypeSettings.configContent) {
            console.log('settings/colorTypeSettings', colorTypeSettings.configContent)
            await store.dispatch('settings/changeSetting', { key: 'colorType', value: colorTypeSettings.configContent })
          }
          // 表格业务颜色
          const colorData = {
            bDefault: true,
            configModule: '10087',
            configName: `local_uiDoc_person_colorModule_${currentUserInfo.tenantId}_${currentUserInfo.userId}_10087`,
            configType: 3, // 固定值，表格配置
            configBy: currentUserInfo.userId,
            tenantId: currentUserInfo.tenantId
          }
          const colorRes = await getPersonQueryConfig(colorData)
          const colorSettings = colorRes.data.find(item => item.bDefault)
          if (colorSettings && colorSettings.configContent) {
            const cacheConfig = store.state.settings.colorSettings
            const remoteConfigs = JSON.parse(colorSettings.configContent)
            console.log('settings/changeSetting', remoteConfigs)
            const configMerged = cacheConfig.reduce((result, config) => {
              const newConfig = { ...config }
              Object.keys(newConfig).forEach(key => {
                const remoteConfig = remoteConfigs.find(conf => conf.colorKey === config.colorKey)
                if (key !== 'colorKey' && remoteConfig && remoteConfig[key] !== undefined) {
                  newConfig[key] = remoteConfig[key]
                }
              })
              result.push(newConfig)
              return result
            }, [])
            await store.dispatch('settings/changeSetting', { key: 'colorSettings', value: configMerged })
          }
          // 表格财务颜色
          const financeColorData = {
            bDefault: true,
            configModule: '10088',
            configName: `local_uiDoc_person_colorModule_${currentUserInfo.tenantId}_${currentUserInfo.userId}_10088`,
            configType: 3, // 固定值，表格配置
            configBy: currentUserInfo.userId,
            tenantId: currentUserInfo.tenantId
          }
          const financeColorRes = await getPersonQueryConfig(financeColorData)
          const financeColorSettings = financeColorRes.data.find(item => item.bDefault)
          if (financeColorSettings && financeColorSettings.configContent) {
            const financeCacheConfig = store.state.settings.financeColorSettings
            const financeRemoteConfigs = JSON.parse(financeColorSettings.configContent)
            console.log('settings/changeSetting', financeRemoteConfigs)
            const financeConfigMerged = financeCacheConfig.reduce((result, config) => {
              const financeNewConfig = { ...config }
              Object.keys(financeNewConfig).forEach(key => {
                const financeRemoteConfig = financeRemoteConfigs.find(conf => conf.colorKey === config.colorKey)
                if (key !== 'colorKey' && financeRemoteConfig && financeRemoteConfig[key] !== undefined) {
                  financeNewConfig[key] = financeRemoteConfig[key]
                }
              })
              result.push(financeNewConfig)
              return result
            }, [])
            await store.dispatch('settings/changeSetting', { key: 'financeColorSettings', value: financeConfigMerged })
          }
          // 中邮用户，默认个性化配置单独设置
          if (currentUserInfo.tenantId === 14615) {
            const newLabelSettings = lodash.cloneDeep(store.state.settings.partnerLabelSettings)
            // 刷【客户编号/供应商编号+中文】
            const customerSupplerNos = newLabelSettings.filter(p => p.customerSupplierNoChineseName !== undefined)
            for (const customerSupplerNo of customerSupplerNos) {
              for (const key in customerSupplerNo) {
                if (typeof customerSupplerNo[key] === 'boolean') {
                  customerSupplerNo[key] = false
                }
              }
              customerSupplerNo.customerSupplierNoChineseName = true
            }
            // 刷【客户编号+中文】
            const customerNos = newLabelSettings.filter(p => p.customerNoChineseName !== undefined)
            for (const customerNo of customerNos) {
              for (const key in customerNo) {
                if (typeof customerNo[key] === 'boolean') {
                  customerNo[key] = false
                }
              }
              customerNo.customerNoChineseName = true
            }
            // 刷【供应商编号+中文】
            const supplerNos = newLabelSettings.filter(p => p.supplierNoChineseName !== undefined)
            for (const supplerNo of supplerNos) {
              for (const key in supplerNo) {
                if (typeof supplerNo[key] === 'boolean') {
                  supplerNo[key] = false
                }
              }
              supplerNo.supplierNoChineseName = true
            }
            await store.dispatch('settings/changeSetting', { key: 'partnerLabelSettings', value: newLabelSettings })
          }
          const labelData = {
            bDefault: true,
            configModule: '10086',
            configName: `local_uiDoc_person_partnerLabel_${currentUserInfo.tenantId}_${currentUserInfo.userId}_10086`,
            configType: 3, // 固定值，表格配置
            configBy: currentUserInfo.userId,
            tenantId: currentUserInfo.tenantId
          }
          const labelRes = await getPersonQueryConfig(labelData)
          const defaultConfig = labelRes.data.find(item => item.bDefault)
          if (defaultConfig && defaultConfig.configContent) {
            const cacheConfig = store.state.settings.partnerLabelSettings
            const remoteConfigs = JSON.parse(defaultConfig.configContent)
            console.log('settings/changeSettingremoteConfig', remoteConfigs)
            const configMerged = cacheConfig.reduce((result, config) => {
              const newConfig = { ...config }
              Object.keys(newConfig).forEach(key => {
                const remoteConfig = remoteConfigs.find(conf => conf.autoKey === config.autoKey)
                if (typeof newConfig[key] === 'boolean' && remoteConfig) {
                  newConfig[key] = remoteConfig[key] || false
                }
              })
              result.push(newConfig)
              return result
            }, [])
            console.log('settings/changeSetting', configMerged)
            await store.dispatch('settings/changeSetting', { key: 'partnerLabelSettings', value: configMerged })
          }
          const ownedBranchStyle = LocalStorageUtilsNew.getConfiguration('ownedBranchStyle', 'string')
          if (ownedBranchStyle) {
            await store.dispatch('settings/changeSetting', { key: 'ownedBranchStyle', value: ownedBranchStyle })
          }

          // 英制单位
          const imperialData = {
            bDefault: true,
            configModule: '25057',
            configName: `local_uiDoc_person_colorModule_${currentUserInfo.tenantId}_${currentUserInfo.userId}_25057`,
            configType: 3, // 固定值，表格配置
            configBy: currentUserInfo.userId,
            tenantId: currentUserInfo.tenantId
          }
          const imperialLabelRes = await getPersonQueryConfig(imperialData)
          const imperialDefaultConfig = imperialLabelRes.data.find(item => item.bDefault)
          if (imperialDefaultConfig && imperialDefaultConfig.configContent) {
            const imperialCacheConfig = store.state.settings.imperialSettings
            const imperialRemoteConfigs = JSON.parse(imperialDefaultConfig.configContent)
            console.log('settings/imperialDataConfig', imperialRemoteConfigs)
            const configMergedI = imperialCacheConfig.reduce((result, config1) => {
              const newConfig1 = { ...config1 }
              Object.keys(newConfig1).forEach(key => {
                const remoteConfig1 = imperialRemoteConfigs.find(conf => conf.key === config1.key)
                if (key !== 'key' && remoteConfig1 && remoteConfig1[key] !== undefined) {
                  newConfig1[key] = remoteConfig1[key] || false
                }
              })
              result.push(newConfig1)
              return result
            }, [])
            console.log('settings/changeSetting', configMergedI)
            await store.dispatch('settings/changeSetting', { key: 'imperialSettings', value: configMergedI })
          }
          cleanLocalStorage(currentUserInfo.tenantId, currentUserInfo.userId)

          await cacheResult
          await printModeResult
          await resetThemeClass()
          refreshCustomField()
          try {
            await bsLimitResult
          } catch (error) {
            console.log('getBsLimitInfo error', error)
          }
          // try {
          //   const uiCustomization = Number(cache.hqConfig.get('UICustomization')) === 1
          //   if (uiCustomization) {
          //     await store.dispatch('uiCustom/initUICustomPageInfo', currentUserInfo.currentUserOfficeList)
          //   }
          // } catch (error) {
          //   console.log('初始化UI自定义页面信息 error', error)
          // }

          // hack method to ensure that addRoutes is complete
          // set the replace: true, so the navigation will not leave a history record
          next({ ...to, replace: true })
          NProgress.done()
        } catch (error) {
          console.log('catch error', error)
          // remove token and go to login page to re-login
          await store.dispatch('user/resetToken')
          console.log('resetToken end')
          Message.error(error || 'Has Error')
          // next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
          next('/login')
          NProgress.done()
        }
      }
    }
  } else {
    /* has no token*/
    if (whiteList.indexOf(to.path) !== -1) {
      // in the free login whitelist, go directly
      next()
    } else {
      if (from.name !== null) {
        Message({
          message: '会话超时，请重新登陆',
          type: 'error',
          duration: 3 * 1000,
          onClose: () => {
            next({ path: '/login', query: { returnPath: to.path }})
          }
        })
      } else {
        next({ path: '/login', query: { returnPath: to.path }})
      }
      // other pages that do not have permission to access are redirected to the login page.
      // next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)

      // next('/401')
      NProgress.done()
    }
  }

  const language = readLanguage()
  const lang = Cookie.get('language') || 'zh'
  if (language) {
    const newLang = language.split('_')[0]
    Cookie.set('language', newLang)
    if (newLang !== lang) {
      window.location.reload()
    }
  }
  /* 路由发生变化修改页面title */
  let currentDomTitle = 'CARGOWARE'
  if (to.meta.title) {
    currentDomTitle = getLangMetaTitle(to.meta)
    /* if (i18n.te(`route.${to.meta.title}`)) {
      currentDomTitle = i18n.t(`route.${to.meta.title}`)
    }*/
  }
  document.title = currentDomTitle
})

router.afterEach(() => {
  // finish progress bar
  NProgress.done()
})
